import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api';
import { useOrg } from '../hooks/useOrg';
import { useAuth } from '../hooks/useAuth';

// Subscription state for the current org. Returns null subscription for orgs
// that aren't on the paid path (law_firm, platform admin) so callers can gate
// uniformly without per-role checks.

const SubscriptionContext = createContext({
  subscription: null,
  loading: true,
  error: null,
  refresh: () => {},
  needsSubscription: false,
});

export function SubscriptionProvider({ children }) {
  const { profile } = useAuth();
  const { currentOrg } = useOrg();
  const [subscription, setSubscription] = useState(null);
  const [billedLocations, setBilledLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isPaidOrg = currentOrg?.type === 'medical_practice';
  const isPlatformAdmin = !!profile?.is_platform_admin;

  const refresh = useCallback(async () => {
    if (!currentOrg) { setLoading(false); return; }
    if (!isPaidOrg || isPlatformAdmin) {
      setSubscription(null); setLoading(false); return;
    }
    setLoading(true); setError(null);
    try {
      const { subscription, locations } = await api.getSubscription(currentOrg.id);
      setSubscription(subscription);
      setBilledLocations(locations || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentOrg, isPaidOrg, isPlatformAdmin]);

  useEffect(() => { refresh(); }, [refresh]);

  // needsSubscription = the gate condition. True when the user is in a
  // medical_practice org that lacks an active or trialing subscription.
  const status = subscription?.status;
  const needsSubscription =
    isPaidOrg && !isPlatformAdmin && !loading &&
    !(status === 'active' || status === 'trialing');

  return (
    <SubscriptionContext.Provider value={{ subscription, billedLocations, loading, error, refresh, needsSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  return useContext(SubscriptionContext);
}
